import { inject, Injectable } from "@angular/core";
import { isStringNullOrEmpty } from "@globals/helper-functions";
import { InterpolationParameters, TranslateService, TranslateStore, TranslationObject } from "@ngx-translate/core";
import { map, Observable, of, switchMap, take } from "rxjs";

@Injectable({
  providedIn: "root"
})
export class BiTranslateService extends TranslateService {
  private readonly translationStore = inject(TranslateStore);

  /**
   * Get the translation for the given key. If the translations are not yet loaded, it will wait for them to be available.
   * @param key The key to retrieve the translation for. For multiple keys, see {@link BiTranslateService.getMulti}
   * @param interpolateParams Optional parameters for interpolation
   * @returns An observable with the translated string
   */
  public override get(key: string, interpolateParams?: InterpolationParameters): Observable<string> {
    if (!key) throw new Error(`Parameter "key" required`);

    return this.getSafe(key, interpolateParams);
  }

  /**
   * Get the translations for the given keys. If the translations are not yet loaded, it will wait for them to be available.
   * @param keys The keys to retrieve translations for
   * @param interpolateParams Optional parameters for interpolation
   * @returns An observable with the translated strings for the given keys, in the same order as the input keys
   */
  public getMulti(keys: string[], interpolateParams?: { [key: string]: string | number }): Observable<string[]> {
    if (!keys) throw new Error(`Parameter "keys" required`);

    return this.getSafe(keys, interpolateParams);
  }

  /**
   * Get the translation for the given key instantly.
   * @param key The key to retrieve the translation for. For multiple keys, see {@link BiTranslateService.instantMulti}
   * @param interpolateParams Optional parameters for interpolation
   * @returns The translated string
   */
  public override instant(key: string, interpolateParams?: InterpolationParameters): string {
    if (!key) throw new Error(`Parameter "key" required`);

    return super.instant(key.toUpperCase(), interpolateParams);
  }

  /**
   * Get the translations for the given keys instantly.
   * @param keys The keys to retrieve translations for
   * @param interpolateParams Optional parameters for interpolation
   * @returns The translated strings for the given keys, in the same order as the input keys
   */
  public instantMulti(keys: string[], interpolateParams?: { [key: string]: string | number }): string[] {
    if (!keys) throw new Error(`Parameter "keys" required`);

    const keysToTranslate = keys.filter(key => !!key).map(key => key.toUpperCase());

    // It is important that we include all original keys, even if some were filtered out, to maintain the correct order in the result
    return this.convertKeysToTranslations(keys, super.instant(keysToTranslate, interpolateParams));
  }

  private getSafe(keyOrKeys: string, interpolateParams?: InterpolationParameters): Observable<string>;
  private getSafe(keyOrKeys: string[], interpolateParams?: InterpolationParameters): Observable<string[]>;
  private getSafe(keyOrKeys: string | string[], interpolateParams?: InterpolationParameters): Observable<string | string[]> {
    if (Array.isArray(keyOrKeys)) {
      if (keyOrKeys.length === 0) return of([]);

      const keysToTranslate = keyOrKeys.filter(key => !!key).map(key => key.toUpperCase());

      const translations$ = this.areTranslationsLoaded()
        ? super.get(keysToTranslate, interpolateParams)
        : this.onLangChange.pipe(
            take(1),
            switchMap(() => super.get(keysToTranslate, interpolateParams))
          );

      // It is important that we include all original keys, even if some were filtered out, to maintain the correct order in the result
      return translations$.pipe(map(translations => this.convertKeysToTranslations(keyOrKeys, translations)));
    }

    const keyToTranslate = keyOrKeys.toUpperCase();

    return this.areTranslationsLoaded()
      ? super.get(keyToTranslate, interpolateParams)
      : this.onLangChange.pipe(
          take(1),
          switchMap(() => super.get(keyToTranslate, interpolateParams))
        );
  }

  private convertKeysToTranslations(keys: string[], translations: TranslationObject): string[] {
    return keys.map(key => (!isStringNullOrEmpty(key) ? (translations[key.toUpperCase()] as string) : ""));
  }

  private areTranslationsLoaded(): boolean {
    const language = this.getCurrentLang();

    return language && this.translationStore.hasTranslationFor(language);
  }
}
